import React, { useState, useEffect, useRef } from 'react';
import {
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  Loader2, ShieldAlert, BarChart2, Activity, Zap,
} from 'lucide-react';
import api from '../services/api';

const fmt = (n) => Number(n).toLocaleString('en-US');

const GRADE_CONFIG = {
  'A+': { label: 'A+ · Healthy',         bg: 'bg-emerald-50',  text: 'text-emerald-700', border: 'border-emerald-200', color: '#16a34a' },
  'A':  { label: 'A · Healthy',          bg: 'bg-emerald-50',  text: 'text-emerald-700', border: 'border-emerald-200', color: '#16a34a' },
  'B':  { label: 'B · Minor Cleanup',    bg: 'bg-lime-50',     text: 'text-lime-700',    border: 'border-lime-200',    color: '#65a30d' },
  'C':  { label: 'C · Needs Cleanup',    bg: 'bg-amber-50',    text: 'text-amber-700',   border: 'border-amber-200',   color: '#f59e0b' },
  'D':  { label: 'D · Action Required',  bg: 'bg-red-50',      text: 'text-red-700',     border: 'border-red-200',     color: '#dc2626' },
};

const UNHEALTHY_COLORS = ['#dc2626', '#ea580c', '#d97706', '#db2777'];

function GaugeChart({ score, color }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;
    ctx.clearRect(0, 0, w, h);
    const cx = w / 2, cy = h - 20, r = Math.min(w, h * 2) / 2 - 10;
    const startAngle = Math.PI, endAngle = 2 * Math.PI;
    // Track
    ctx.beginPath();
    ctx.arc(cx, cy, r, startAngle, endAngle);
    ctx.lineWidth = 18;
    ctx.strokeStyle = '#e5e7eb';
    ctx.stroke();
    // Fill
    const fillEnd = startAngle + (score / 100) * Math.PI;
    ctx.beginPath();
    ctx.arc(cx, cy, r, startAngle, fillEnd);
    ctx.lineWidth = 18;
    ctx.strokeStyle = color;
    ctx.lineCap = 'round';
    ctx.stroke();
  }, [score, color]);

  return <canvas ref={canvasRef} width={260} height={150} />;
}

function KpiCard({ label, value, sub, valueColor = 'text-gray-900' }) {
  return (
    <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
      <p className={`text-2xl font-bold tabular-nums ${valueColor}`}>{value}</p>
      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mt-0.5">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function SectionHeader({ title, sub }) {
  return (
    <div className="mb-4">
      <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider">{title}</h2>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function HBar({ label, value, max, color, index }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-600 font-medium flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: color }} />
          {label}
        </span>
        <span className="font-bold tabular-nums" style={{ color }}>{fmt(value)}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

export default function APLHealthDashboard() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    api.get('/reports/fis/apl-guru/dashboard')
      .then(res => setData(res.data?.data || res.data))
      .catch(() => setError('Failed to load APL Health data.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 animate-spin text-amber-500 mr-2" />
      <span className="text-sm text-gray-500">Loading APL Health Dashboard...</span>
    </div>
  );

  if (error) return (
    <div className="flex items-center justify-center h-64 text-red-500 text-sm">
      <AlertTriangle className="w-5 h-5 mr-2" /> {error}
    </div>
  );

  if (!data) return null;

  const { meta, current, prior, unhealthy, prohibited, delta, industry, findings } = data;
  const grade   = GRADE_CONFIG[current.grade] || GRADE_CONFIG['A'];
  const delta_h = current.health - (prior.health || current.health);
  const maxUnhealthy = Math.max(...unhealthy.map(u => u.violations), 1);

  return (
    <div className="p-6 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: 'radial-gradient(circle at 35% 35%, #FFD55A, #F59E0B)' }} />
            APL Health Dashboard
          </h1>
          <p className="text-xs text-gray-400 mt-0.5">
            FIS Standard APL · Source: <span className="font-medium text-gray-600">{meta.aplFile}</span>
            {' · '}Nightly run <span className="font-medium text-gray-600">{meta.runDate}</span>
          </p>
        </div>
        <div className={`px-3 py-1.5 rounded-full border text-xs font-bold ${grade.bg} ${grade.text} ${grade.border}`}>
          {grade.label}
        </div>
      </div>

      {/* Row 1 — Health Score + KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-[360px_1fr] gap-5">

        {/* Health Score Card */}
        <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm text-center">
          <SectionHeader title="APL Health Score" />
          <div className="flex justify-center">
            <div className="relative">
              <GaugeChart score={current.health} color={grade.color} />
              <div className="absolute inset-x-0 bottom-6 text-center pointer-events-none">
                <p className="text-4xl font-black tabular-nums" style={{ color: grade.color }}>
                  {current.health.toFixed(1)}<span className="text-lg font-bold text-gray-400">%</span>
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-1.5 text-sm mt-1">
            {delta_h >= 0
              ? <TrendingUp className="w-4 h-4 text-emerald-500" />
              : <TrendingDown className="w-4 h-4 text-red-500" />}
            <span className={`font-semibold ${delta_h >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {delta_h >= 0 ? '+' : ''}{delta_h.toFixed(2)} pts vs prior APL
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            Violations {fmt(prior.violations)} → {fmt(current.violations)}
          </p>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <KpiCard label="Total SKUs"      value={fmt(current.totalSkus)}        valueColor="text-gray-900" />
            <KpiCard label="Violations"      value={fmt(current.violations)}       valueColor="text-red-600" sub="Purchasable (5A/5D)" />
            <KpiCard label="Remove List"     value={fmt(current.removeUnhealthy)}  valueColor="text-red-700" sub="Unhealthy food" />
            <KpiCard label="Recode List"     value={fmt(current.recodeProhibited)} valueColor="text-amber-600" sub="Prohibited → XX" />
          </div>

          <p className="text-[11px] text-gray-400 mt-4 leading-relaxed">
            <b>Remove</b> = unhealthy food coded 5A/5D. <b>Recode</b> = prohibited items coded 5A/5D that must be blocked.
          </p>
        </div>

        {/* Unhealthy Subcategories */}
        <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
          <SectionHeader
            title="Unhealthy Subcategories · Remove List"
            sub="Purchasable (5A/5D) SKUs to send to FIS for removal from the APL."
          />
          <div className="mb-5">
            {[...unhealthy].sort((a, b) => b.violations - a.violations).map((u, i) => (
              <HBar key={u.key} label={u.key} value={u.violations} max={maxUnhealthy} color={UNHEALTHY_COLORS[i]} index={i} />
            ))}
          </div>
          <table className="w-full text-sm border-t border-gray-100 pt-3">
            <thead>
              <tr>
                <th className="text-left text-[11px] uppercase tracking-wide text-gray-400 font-semibold pb-2">Subcategory</th>
                <th className="text-right text-[11px] uppercase tracking-wide text-gray-400 font-semibold pb-2">SKUs to Remove</th>
              </tr>
            </thead>
            <tbody>
              {[...unhealthy].sort((a, b) => b.violations - a.violations).map((u, i) => (
                <tr key={u.key} className="border-t border-gray-50">
                  <td className="py-2 text-gray-700 flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: UNHEALTHY_COLORS[i] }} />
                    {u.key}
                  </td>
                  <td className="py-2 text-right font-bold tabular-nums text-red-600">{fmt(u.violations)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-gray-200">
                <td className="py-2 font-bold text-gray-800">Total to Remove</td>
                <td className="py-2 text-right font-black tabular-nums text-red-700">
                  {fmt(unhealthy.reduce((s, u) => s + u.violations, 0))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Row 2 — Prohibited Items + Delta */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

        {/* Prohibited Items */}
        <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
          <SectionHeader
            title="Prohibited Items · Recode List"
            sub="Purchasable (5A/5D) recommended for recoding to XX."
          />
          <div className="space-y-3 mb-5">
            {prohibited.map(p => {
              const total = p.purchasable + p.blocked;
              const purchPct = total > 0 ? (p.purchasable / total) * 100 : 0;
              const blockPct = total > 0 ? (p.blocked / total) * 100 : 0;
              return (
                <div key={p.key}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium text-gray-600">{p.key}</span>
                    <span className="text-gray-400 tabular-nums">{fmt(total)} total</span>
                  </div>
                  <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden flex">
                    <div className="h-full bg-red-500 rounded-l-full transition-all duration-500" style={{ width: `${purchPct}%` }} />
                    <div className="h-full bg-emerald-500 rounded-r-full transition-all duration-500" style={{ width: `${blockPct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-4 text-xs text-gray-500 mb-4">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-red-500 inline-block" />Purchasable (to XX)</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" />Blocked (XX)</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left text-[11px] uppercase tracking-wide text-gray-400 font-semibold pb-2">Category</th>
                <th className="text-right text-[11px] uppercase tracking-wide text-gray-400 font-semibold pb-2">Purchasable (to XX)</th>
                <th className="text-right text-[11px] uppercase tracking-wide text-gray-400 font-semibold pb-2">Blocked (XX)</th>
              </tr>
            </thead>
            <tbody>
              {prohibited.map(p => (
                <tr key={p.key} className="border-t border-gray-100">
                  <td className="py-2 text-gray-700">{p.key}</td>
                  <td className="py-2 text-right font-bold tabular-nums text-red-600">{fmt(p.purchasable)}</td>
                  <td className="py-2 text-right font-semibold tabular-nums text-emerald-600">{fmt(p.blocked)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Delta Since Last APL */}
        <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
          <SectionHeader
            title="Delta Since Last APL"
            sub={`${delta.old_file?.replace('SUNNY APL_', '').replace('.csv', '')} → ${delta.new_file?.replace('SUNNY APL_', '').replace('.csv', '')}`}
          />
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-center">
              <p className="text-2xl font-black tabular-nums text-red-600">+{fmt(delta.added)}</p>
              <p className="text-[11px] font-semibold text-red-400 uppercase tracking-wide mt-0.5">Added</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-center">
              <p className="text-2xl font-black tabular-nums text-emerald-600">−{fmt(delta.removed)}</p>
              <p className="text-[11px] font-semibold text-emerald-400 uppercase tracking-wide mt-0.5">Removed</p>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-center">
              <p className="text-2xl font-black tabular-nums text-blue-600">{fmt(delta.recoded)}</p>
              <p className="text-[11px] font-semibold text-blue-400 uppercase tracking-wide mt-0.5">Recoded</p>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left text-[11px] uppercase tracking-wide text-gray-400 font-semibold pb-2">Movement (new 5A/5D items)</th>
                <th className="text-right text-[11px] uppercase tracking-wide text-gray-400 font-semibold pb-2">SKUs</th>
                <th className="text-right text-[11px] uppercase tracking-wide text-gray-400 font-semibold pb-2">FIS Action</th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'Added unhealthy food',  value: delta.added_unhealthy,  action: 'Remove from APL',      cls: 'text-red-600' },
                { label: 'Added prohibited items', value: delta.added_prohibited, action: 'Recode to XX',         cls: 'text-red-600' },
                { label: 'Fixed to XX by FIS',     value: delta.fixed_to_xx,      action: 'Already corrected',    cls: 'text-emerald-600' },
                { label: 'Regressed from XX',      value: delta.regressed_from_xx, action: 'None became purchasable', cls: 'text-gray-500' },
              ].map(r => (
                <tr key={r.label} className="border-t border-gray-100">
                  <td className="py-2 text-gray-700 font-medium">{r.label}</td>
                  <td className={`py-2 text-right font-bold tabular-nums ${r.cls}`}>{fmt(r.value)}</td>
                  <td className="py-2 text-right text-gray-400 text-xs">{r.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Row 3 — Industry Benchmark */}
      <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
        <SectionHeader
          title="Industry Benchmark · Nightly Research"
          sub="New launches researched per category, scrubbed against the APL. Confirmed hits flow into the FIS lists above."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Chart */}
          <div className="space-y-3">
            {industry.map(cat => {
              const maxTracked = Math.max(...industry.map(c => c.tracked), 1);
              return (
                <div key={cat.key}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium text-gray-600">{cat.label}</span>
                    <span className="text-gray-400">{cat.found}/{cat.tracked} found</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex gap-0.5">
                    <div className="h-full bg-amber-400 rounded-full transition-all duration-500"
                      style={{ width: `${(cat.tracked / maxTracked) * 100}%` }} />
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex gap-0.5 mt-0.5">
                    <div className="h-full bg-blue-500 rounded-full transition-all duration-500"
                      style={{ width: `${(cat.found / maxTracked) * 100}%` }} />
                  </div>
                </div>
              );
            })}
            <div className="flex gap-4 text-xs text-gray-500 mt-2">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2 rounded-sm bg-amber-400 inline-block" />Tracked</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2 rounded-sm bg-blue-500 inline-block" />Found on APL</span>
            </div>
          </div>

          {/* Table */}
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left text-[11px] uppercase tracking-wide text-gray-400 font-semibold pb-2">Category</th>
                <th className="text-right text-[11px] uppercase tracking-wide text-gray-400 font-semibold pb-2">Tracked</th>
                <th className="text-right text-[11px] uppercase tracking-wide text-gray-400 font-semibold pb-2">Found</th>
                <th className="text-right text-[11px] uppercase tracking-wide text-gray-400 font-semibold pb-2">Purchasable</th>
              </tr>
            </thead>
            <tbody>
              {industry.map(cat => (
                <tr key={cat.key} className="border-t border-gray-100">
                  <td className="py-2 text-gray-700">{cat.label}</td>
                  <td className="py-2 text-right tabular-nums text-gray-600">{cat.tracked}</td>
                  <td className="py-2 text-right tabular-nums text-blue-600 font-semibold">{cat.found}</td>
                  <td className={`py-2 text-right tabular-nums font-bold ${cat.purchasable > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {fmt(cat.purchasable)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Findings callouts */}
      {findings && (
        <div className="space-y-3">
          {findings.alert && (
            <div className="rounded-xl p-3.5 border border-red-200 bg-red-50 text-red-900 text-xs leading-relaxed">
              <span className="font-bold">Alert: </span>{findings.alert}
            </div>
          )}
          {findings.gap && (
            <div className="rounded-xl p-3.5 border border-amber-200 bg-amber-50 text-amber-900 text-xs leading-relaxed">
              <span className="font-bold">Detection update: </span>{findings.gap}
            </div>
          )}
          {findings.info && (
            <div className="rounded-xl p-3.5 border border-blue-200 bg-blue-50 text-blue-900 text-xs leading-relaxed">
              <span className="font-bold">Info: </span>{findings.info}
            </div>
          )}
        </div>
      )}

      <p className="text-center text-[11px] text-gray-300">
        Generated by APL Guru · Nightly: research → scrub → store Remove + Recode lists · Violation = flagged category coded 5A/5D
      </p>
    </div>
  );
}
